import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  assignAssessor,
  countAnswersByRun,
  createEngagement,
  createEntity,
  createRun,
  inviteMember,
  listAssignmentsForOrganisation,
  listEntities,
  listMyMemberships,
  listOrganisations,
  listPublishedVersions,
  listRunsForOrganisation,
  memberDirectory,
  sendSignInLink,
} from "../lib/api";
import { clientError } from "../lib/clientMessage";
import {
  INVITATION_TEXT,
  INVITATION_TEXT_MAX,
  KNOWN_PERSON_TEXT,
  NOT_A_MEMBER,
  NOT_INVITED,
  invitationText,
  isNotInvitedError,
  STATUS_LABEL,
  defaultNames,
  invitationRows,
  looksLikeEmail,
  matchMember,
  type InvitationRow,
} from "../lib/invite";
import type { MemberDirectoryEntry, Organisation, PublishedVersion } from "../lib/types";
import { useBreadcrumb } from "../shell/ContextHeader";

// Invitations — DESIGN_SLICE_INVITE_SCREEN 281026 V1.0, the administrator's
// one screen: client, email, assessment, Send invitation. The press creates
// what is missing (the PMO function, an engagement, the run, the assignment)
// through the owner's own authority under RLS, then sends the member one
// mail whose link signs them in. Below, the invitations and their status.
//
// The line the database draws, kept: a member who is not yet on the roster
// is added by the governed path (0018/0020/0023), never here. For a person
// LINNFOSS has recorded an invitation for (provision.py invite), the first
// press sends the magic link with shouldCreateUser: Supabase Auth creates the
// identity and 0023's trigger grants the membership in the same transaction,
// so the roster shows the person before the run is assigned. Without a
// recorded invitation the database refuses the identity and the press says so.
//
// The invitation's text (DESIGN_SLICE_INVITATION_TEXT 301026 V1.0): the box is
// prefilled with the standard greeting; a new person's mail carries what it
// says, a known person's carries the standard greeting.

type Ready = {
  orgs: Organisation[];
  versions: PublishedVersion[];
  orgId: string;
  roster: MemberDirectoryEntry[];
  rows: InvitationRow[];
};

type State = { kind: "loading" } | { kind: "error"; message: string } | ({ kind: "ready" } & Ready);

export function Invitations() {
  const { setTrail } = useBreadcrumb();
  const [state, setState] = useState<State>({ kind: "loading" });
  const [email, setEmail] = useState("");
  const [versionId, setVersionId] = useState("");
  const [text, setText] = useState(INVITATION_TEXT);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ tone: "ok" | "warn"; text: string } | null>(null);

  useEffect(() => setTrail([{ label: "Invitations" }]), [setTrail]);

  const loadOrg = useCallback(async (orgId: string) => {
    const [roster, runs, assignments] = await Promise.all([
      memberDirectory(orgId),
      listRunsForOrganisation(orgId),
      listAssignmentsForOrganisation(orgId),
    ]);
    const answers = await countAnswersByRun(runs.map((r) => r.id));
    return { roster, rows: invitationRows({ runs, assignments, roster, answers }) };
  }, []);

  const load = useCallback(async (preferredOrg?: string) => {
    try {
      const [orgs, memberships, versions] = await Promise.all([listOrganisations(), listMyMemberships(), listPublishedVersions()]);
      const administered = orgs.filter((o) => memberships.some((m) => m.organisation_id === o.id && (m.role === "owner" || m.role === "lead_assessor")));
      const orgId = preferredOrg && administered.some((o) => o.id === preferredOrg) ? preferredOrg : (administered[0]?.id ?? "");
      const part = orgId ? await loadOrg(orgId) : { roster: [], rows: [] };
      setState({ kind: "ready", orgs: administered, versions, orgId, ...part });
      setVersionId((v) => v || (versions.find((x) => x.instruments?.slug === "pmo-self-assessment")?.id ?? versions[0]?.id ?? ""));
    } catch (e) {
      setState({ kind: "error", message: clientError("load", e).message });
    }
  }, [loadOrg]);

  useEffect(() => { void load(); }, [load]);

  if (state.kind === "loading") return <p>Loading…</p>;
  if (state.kind === "error") return <div className="error-banner" role="alert">{state.message}</div>;

  const { orgs, versions, orgId, roster, rows } = state;
  const org = orgs.find((o) => o.id === orgId) ?? null;
  const member = matchMember(email, roster);
  const version = versions.find((v) => v.id === versionId) ?? null;
  const emailShaped = looksLikeEmail(email);
  const canSend = !!org && emailShaped && !!version && !busy;

  async function send(e: FormEvent) {
    e.preventDefault();
    if (!org || !emailShaped || !version) return;
    setBusy(true);
    setNote(null);
    try {
      let target = member;
      let mailSent = false;
      if (!target) {
        // A new person: the press records the invitation (0024: the caller
        // administers this client), then the mail that creates the identity
        // goes first, so that the membership exists (0023's claim) when the
        // run is assigned below.
        await inviteMember(org.id, email.trim(), "assessor");
        try {
          await sendSignInLink(email.trim(), true, invitationText(text));
        } catch (err) {
          if (err instanceof Error && isNotInvitedError(err.message)) {
            setNote({ tone: "warn", text: NOT_INVITED });
            return;
          }
          throw err;
        }
        mailSent = true;
        target = matchMember(email, await memberDirectory(org.id));
        if (!target) throw new Error("the identity was created but the roster does not show it");
      }
      const names = defaultNames(new Date());
      // The one PMO function of the client, created only when missing (0019: one per organisation).
      const entities = await listEntities(org.id);
      const entity =
        entities.find((x) => x.kind === "pmo_function" && x.status === "active") ??
        (await createEntity({ organisation_id: org.id, kind: "pmo_function", name: names.entity }));
      const engagement = await createEngagement({ organisation_id: org.id, entity_id: entity.id, name: names.engagement });
      const run = await createRun({ organisation_id: org.id, engagement_id: engagement.id, instrument_version_id: version.id, title: names.run });
      await assignAssessor(run.id, org.id, target.user_id, false);
      if (!mailSent) await sendSignInLink(target.email);
      setNote({ tone: "ok", text: `Invitation sent. The link in the mail opens the assessment and is valid for 24 hours.` });
      setEmail("");
      setText(INVITATION_TEXT);
      const part = await loadOrg(org.id);
      setState({ kind: "ready", orgs, versions, orgId: org.id, ...part });
    } catch (err) {
      setNote({ tone: "warn", text: clientError("save", err).message });
    } finally {
      setBusy(false);
    }
  }

  async function resend(row: InvitationRow) {
    if (!row.email) return;
    setBusy(true);
    setNote(null);
    try {
      await sendSignInLink(row.email);
      setNote({ tone: "ok", text: "A fresh link is on its way. It is valid for 24 hours." });
    } catch (err) {
      setNote({ tone: "warn", text: clientError("save", err).message });
    } finally {
      setBusy(false);
    }
  }

  async function switchOrg(id: string) {
    setState({ kind: "loading" });
    setNote(null);
    await load(id);
  }

  return (
    <>
      <h1 style={{ marginBottom: "0.25rem" }}>Invite a respondent</h1>
      <p style={{ marginTop: 0, color: "var(--text-muted)" }}>One press: the assessment is set up and the respondent gets a mail whose link opens it.</p>

      <form className="card" onSubmit={send} style={{ display: "grid", gap: "0.9rem" }}>
        <label style={{ display: "grid", gap: "0.3rem" }}>
          <span className="field-label">Client</span>
          <select value={orgId} onChange={(e) => void switchOrg(e.target.value)} disabled={busy}>
            {orgs.length === 0 && <option value="">No client you administer</option>}
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </label>

        <label style={{ display: "grid", gap: "0.3rem" }}>
          <span className="field-label">Email</span>
          <input
            id="invite-email"
            type="email"
            list="invite-roster"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@client.example"
            autoComplete="off"
            disabled={busy || !org}
          />
          <datalist id="invite-roster">
            {roster.map((m) => (
              <option key={m.user_id} value={m.email} />
            ))}
          </datalist>
          {email && emailShaped && !member && (
            <span style={{ color: "var(--state-warn)", fontSize: "var(--lf-body-size)" }}>{NOT_A_MEMBER}</span>
          )}
        </label>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.9rem" }}>
          <label style={{ display: "grid", gap: "0.3rem" }}>
            <span className="field-label">Role</span>
            <select value="respondent" disabled>
              <option value="respondent">Respondent</option>
            </select>
          </label>
          <label style={{ display: "grid", gap: "0.3rem" }}>
            <span className="field-label">Assessment</span>
            <select value={versionId} onChange={(e) => setVersionId(e.target.value)} disabled={busy}>
              {versions.map((v) => (
                <option key={v.id} value={v.id}>{v.instruments?.title ?? v.instruments?.slug ?? "Instrument"} {v.version}</option>
              ))}
            </select>
          </label>
        </div>

        <label style={{ display: "grid", gap: "0.3rem" }}>
          <span className="field-label">Text in the mail</span>
          <textarea
            id="invite-text"
            value={member ? INVITATION_TEXT : text}
            onChange={(e) => setText(e.target.value)}
            maxLength={INVITATION_TEXT_MAX}
            rows={4}
            readOnly={!!member}
            disabled={busy || !org}
          />
          {member && <span style={{ color: "var(--text-muted)", fontSize: "var(--lf-body-size)" }}>{KNOWN_PERSON_TEXT}</span>}
        </label>

        <div style={{ display: "flex", alignItems: "center", gap: "0.9rem", flexWrap: "wrap" }}>
          <button className="btn" type="submit" disabled={!canSend}>
            {busy ? "Sending…" : "Send invitation"}
          </button>
          <span style={{ color: "var(--text-muted)", fontSize: "var(--lf-body-size)" }}>
            Creates the client's PMO function if missing, an engagement, the run and the assignment, and sends one mail.
          </span>
        </div>
        {note && (
          <p className={note.tone === "warn" ? "error-banner" : undefined} role={note.tone === "warn" ? "alert" : "status"} style={{ margin: 0 }}>
            {note.text}
          </p>
        )}
      </form>

      <section className="card" style={{ marginTop: "1rem" }}>
        <h2 style={{ marginTop: 0 }}>Invitations</h2>
        {rows.length === 0 ? (
          <p style={{ margin: 0, color: "var(--text-muted)" }}>None yet for this client.</p>
        ) : (
          <table className="report-table">
            <thead>
              <tr><th>Email</th><th>Assessment</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const v = versions.find((x) => x.id === r.instrumentVersionId);
                return (
                  <tr key={r.runId}>
                    <td>{r.email ?? <span style={{ color: "var(--text-muted)" }}>not assigned</span>}</td>
                    <td>{v ? `${v.instruments?.title ?? v.instruments?.slug ?? ""} ${v.version}` : r.runTitle}</td>
                    <td>{STATUS_LABEL[r.status]}</td>
                    <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                      {r.status === "report_ready" ? (
                        <Link className="btn btn-secondary" to={`/clients/${orgId}/runs/${r.runId}/dashboard`}>Open dashboard</Link>
                      ) : r.email ? (
                        <button className="btn btn-secondary" type="button" disabled={busy} onClick={() => void resend(r)}>Resend</button>
                      ) : (
                        <Link className="btn-link" to={`/clients/${orgId}/runs/${r.runId}`}>Open run</Link>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}
